import React, { useState } from "react";
import { X, Plus, Tag as TagIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function TagsEditor({ docId, value = [], onChange }) {
  const { t } = useTranslation("components");
  const [tags, setTags] = useState(value);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const persist = async (next) => {
    setBusy(true);
    try {
      const res = await api.patch(`/documents/${docId}`, { tags: next });
      const updated = res.data?.tags ?? next;
      setTags(updated);
      onChange?.(updated);
    } catch (e) {
      console.error(e);
      toast.error("Could not update tags");
      setTags(value);
    } finally {
      setBusy(false);
    }
  };

  const commitAdd = () => {
    const t = draft.trim();
    setDraft("");
    setAdding(false);
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      toast.message("Tag already exists");
      return;
    }
    if (tags.length >= 12) {
      toast.error("Maximum 12 tags per document");
      return;
    }
    persist([...tags, t]);
  };

  const remove = (t) => {
    persist(tags.filter((x) => x !== t));
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="tags-editor">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-500 mr-1">
        <TagIcon size={12} strokeWidth={1.75} />
        <span>{t("tags.section_label")}</span>
      </div>
      {tags.map((t) => (
        <span
          key={t}
          data-testid={`tag-chip-${t}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-xs text-zinc-700"
        >
          {t}
          <button
            onClick={() => remove(t)}
            disabled={busy}
            data-testid={`tag-remove-${t}`}
            aria-label={`Remove tag ${t}`}
            className="text-zinc-400 hover:text-zinc-900 transition-colors disabled:opacity-50"
          >
            <X size={11} strokeWidth={2} />
          </button>
        </span>
      ))}
      {adding ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitAdd}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitAdd();
            if (e.key === "Escape") {
              setDraft("");
              setAdding(false);
            }
          }}
          placeholder={t("tags.add_placeholder")}
          data-testid="tag-input"
          className="h-6 px-2 text-xs rounded-full border border-zinc-300 bg-white focus:outline-none focus:ring-1 focus:ring-zinc-950 w-28"
        />
      ) : (
        <button
          onClick={() => setAdding(true)}
          data-testid="add-tag-button"
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-zinc-300 bg-white px-2.5 py-1 text-xs text-zinc-500 hover:text-zinc-900 hover:border-zinc-400 transition-colors"
        >
          <Plus size={11} strokeWidth={2} /> {t("tags.add")}
        </button>
      )}
    </div>
  );
}
